import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Seed data so the ticker never renders empty (replaced by live data instantly)
const SEED = [
  { symbol: 'GOLD', name: 'Gold', price: 4093, change: -0.7, gold: true },
  { symbol: 'BTC', name: 'Bitcoin', price: 82700, change: -0.12 },
  { symbol: 'ETH', name: 'Ethereum', price: 3100, change: -1.2 },
  { symbol: 'SOL', name: 'Solana', price: 130, change: -0.8 },
  { symbol: 'XRP', name: 'XRP', price: 2.0, change: -0.5 },
  { symbol: 'ADA', name: 'Cardano', price: 0.6, change: -0.4 },
];

const CryptoTicker = () => {
  const [cryptoData, setCryptoData] = useState(SEED);

  useEffect(() => {
    let active = true;
    const fetchPrices = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/markets`);
        if (!res.ok) return;
        const json = await res.json();
        if (active && json.markets && json.markets.length) {
          setCryptoData(
            json.markets.map((m) => ({
              symbol: m.symbol,
              name: m.name,
              price: m.usd,
              change: m.usd_24h_change ?? 0,
              gold: m.id === 'gold',
            }))
          );
        }
      } catch (e) {
        // keep showing last known data — never go blank
      }
    };

    fetchPrices();
    const interval = setInterval(fetchPrices, 20000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, []);

  // Duplicate items for seamless loop
  const tickerItems = [...cryptoData, ...cryptoData, ...cryptoData];

  return (
    <div className="w-full bg-black/60 backdrop-blur-sm border-y border-white/[0.06]" data-testid="crypto-ticker">
      <div className="relative overflow-hidden h-10 md:h-12">
        {/* Live indicator */}
        <div className="absolute left-0 top-0 bottom-0 w-20 md:w-32 bg-gradient-to-r from-black via-black/90 to-transparent z-10 flex items-center pl-3 md:pl-4">
          <div className="flex items-center gap-1.5">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
            <span className="text-[9px] md:text-[10px] text-white/50 font-medium tracking-wider hidden md:inline">LIVE</span>
          </div>
        </div>

        {/* Fade right */}
        <div className="absolute right-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-l from-black via-black/90 to-transparent z-10" />

        {/* Scrolling ticker */}
        <div className="flex items-center h-full animate-ticker">
          {tickerItems.map((crypto, index) => (
            <div
              key={`${crypto.symbol}-${index}`}
              className="flex items-center gap-2 md:gap-3 px-4 md:px-6 whitespace-nowrap"
            >
              {/* Symbol */}
              <span
                className={`text-[10px] md:text-xs font-semibold tracking-wide ${
                  crypto.gold ? 'text-amber-300' : 'text-white/80'
                }`}
              >
                {crypto.symbol}
              </span>

              {/* Price */}
              <span className="text-white/60 text-[10px] md:text-xs font-light tabular-nums">
                ${crypto.price > 1000
                  ? crypto.price.toLocaleString(undefined, { maximumFractionDigits: 0 })
                  : crypto.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>

              {/* Change */}
              <span className={`flex items-center gap-0.5 text-[9px] md:text-[10px] font-medium ${
                crypto.change >= 0 ? 'text-green-400' : 'text-red-400'
              }`}>
                {crypto.change >= 0 ? (
                  <TrendingUp className="w-2.5 h-2.5 md:w-3 md:h-3" />
                ) : (
                  <TrendingDown className="w-2.5 h-2.5 md:w-3 md:h-3" />
                )}
                {Math.abs(crypto.change).toFixed(2)}%
              </span>

              {/* Separator dot */}
              <span className="text-white/10 text-xs ml-2 md:ml-4">•</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CryptoTicker;
